import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
import { FileUpload } from '../components/FileUpload';
import { UserIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface FormErrors {
  displayName?: string;
  bio?: string;
  website?: string;
  twitter?: string;
  bluesky?: string;
}

export const WriterProfileEditPage = () => {
  const [formData, setFormData] = useState({
    profilePhoto: null as File | null,
    displayName: '',
    bio: '',
    primaryGenre: '',
    experienceLevel: '',
    website: '',
    twitter: '',
    bluesky: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.users.profile, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to load profile');
        setLoading(false);
        return;
      }

      const user = data.user;
      setFormData({
        profilePhoto: user.profilePhoto || null,
        displayName: user.displayName || '',
        bio: user.bio || '',
        primaryGenre: user.writerProfile?.primaryGenre || '',
        experienceLevel: user.writerProfile?.experienceLevel || '',
        website: user.website || '',
        twitter: user.twitter || '',
        bluesky: '', // Not in schema yet
      });

      setLoading(false);
    } catch (err) {
      console.error('Fetch profile error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const genres = [
    { value: 'literary-fiction', label: 'Literary Fiction' },
    { value: 'mystery', label: 'Mystery' },
    { value: 'thriller', label: 'Thriller' },
    { value: 'romance', label: 'Romance' },
    { value: 'fantasy', label: 'Fantasy' },
    { value: 'science-fiction', label: 'Science Fiction' },
    { value: 'historical-fiction', label: 'Historical Fiction' },
    { value: 'horror', label: 'Horror' },
    { value: 'young-adult', label: 'Young Adult' },
    { value: 'memoir', label: 'Memoir' },
    { value: 'other', label: 'Other' },
  ];

  const experienceLevels = [
    { value: 'first-manuscript', label: 'This is my first manuscript' },
    { value: 'some-experience', label: 'I\'ve written a few manuscripts' },
    { value: 'experienced', label: 'I\'m an experienced writer' },
    { value: 'published', label: 'I\'m published' },
  ];

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.displayName) {
      newErrors.displayName = 'Display name is required';
    }

    if (formData.bio && formData.bio.length > 500) {
      newErrors.bio = 'Bio must be 500 characters or less';
    }

    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Please enter a valid URL (starting with http:// or https://)';
    }

    if (formData.twitter && !formData.twitter.match(/^@?\w+$/)) {
      newErrors.twitter = 'Please enter a valid Twitter handle';
    }

    if (formData.bluesky && !formData.bluesky.match(/^@?[\w.-]+\.bsky\.social$/)) {
      newErrors.bluesky = 'Please enter a valid Bluesky handle (e.g., @user.bsky.social)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const response = await fetch(API_ENDPOINTS.users.updateProfile, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          displayName: formData.displayName,
          bio: formData.bio,
          website: formData.website,
          twitter: formData.twitter,
          writerProfile: {
            primaryGenre: formData.primaryGenre || null,
            experienceLevel: formData.experienceLevel || null,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.error || 'Failed to save profile');
        setIsSaving(false);
        return;
      }

      // Update localStorage with new display name
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.displayName = formData.displayName;
        localStorage.setItem('user', JSON.stringify(user));
      }

      window.alert('Profile saved successfully!');
      setIsSaving(false);
    } catch (err) {
      console.error('Save profile error:', err);
      window.alert('Network error. Please try again.');
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to discard your changes?')) {
      window.location.href = '/writer/dashboard';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Edit Profile
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Update your profile information and writing details
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xlarge,
            padding: '60px 40px',
            border: `1px solid ${theme.colors.border}`,
            textAlign: 'center',
          }}>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Loading profile...
            </p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.xlarge,
            padding: '40px',
            border: `1px solid ${theme.colors.border}`,
            marginBottom: '24px',
          }}>
            <p style={{
              fontSize: '18px',
              color: theme.colors.error,
              marginBottom: '20px',
              textAlign: 'center',
            }}>
              {error}
            </p>
            <div style={{ textAlign: 'center' }}>
              <Button onClick={fetchProfile}>
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Form Card */}
        {!loading && !error && (
        <>
        <div style={{
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.xlarge,
          padding: '40px',
          border: `1px solid ${theme.colors.border}`,
          marginBottom: '24px',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
            {/* Profile Photo */}
            <div>
              <FileUpload
                label="Profile Photo"
                onChange={(file) => setFormData({ ...formData, profilePhoto: file })}
                helperText="Recommended: Square image, at least 400x400px"
              />
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Basic Information */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '500',
                marginBottom: '20px',
                color: theme.colors.textPrimary,
              }}>
                Basic Information
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}>
                <Input
                  label="Display Name"
                  type="text"
                  placeholder="Your name"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  error={errors.displayName}
                  icon={<UserIcon />}
                  required
                />

                <Textarea
                  label="Bio"
                  placeholder="Tell readers about yourself and your writing..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  maxLength={500}
                  rows={5}
                  error={errors.bio}
                  helperText="A brief introduction that appears on your public profile"
                />
              </div>
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Writing Details */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '500',
                marginBottom: '20px',
                color: theme.colors.textPrimary,
              }}>
                Writing Details
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}>
                <Select
                  label="Primary Genre"
                  value={formData.primaryGenre}
                  onChange={(e) => setFormData({ ...formData, primaryGenre: e.target.value })}
                  options={genres}
                  placeholder="Select your main genre"
                  helperText="This helps beta readers find your work"
                />

                <Select
                  label="Experience Level"
                  value={formData.experienceLevel}
                  onChange={(e) => setFormData({ ...formData, experienceLevel: e.target.value })}
                  options={experienceLevels}
                  placeholder="Select your experience level"
                />
              </div>
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Social Links */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '500',
                marginBottom: '20px',
                color: theme.colors.textPrimary,
              }}>
                Social Links
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}>
                <Input
                  label="Website"
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  error={errors.website}
                  helperText="Optional - Your personal website or blog"
                />

                <Input
                  label="Twitter"
                  type="text"
                  placeholder="@yourusername"
                  value={formData.twitter}
                  onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                  error={errors.twitter}
                  helperText="Optional - Your Twitter/X handle"
                />

                <Input
                  label="Bluesky"
                  type="text"
                  placeholder="@yourusername.bsky.social"
                  value={formData.bluesky}
                  onChange={(e) => setFormData({ ...formData, bluesky: e.target.value })}
                  error={errors.bluesky}
                  helperText="Optional - Your Bluesky handle"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '16px',
        }}>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Help Text */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: theme.colors.backgroundAlt,
          borderRadius: theme.borderRadius.large,
          fontSize: '15px',
          lineHeight: '1.6',
          color: theme.colors.textSecondary,
        }}>
          <strong style={{ color: theme.colors.textPrimary }}>Tip:</strong> A complete
          profile helps beta readers understand your background and connect with your work.
          The more information you provide, the better matches you'll receive!
        </div>
        </>
        )}
      </main>

      <Footer />
    </div>
  );
};
